import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Btn, Spinner } from '../components/UI';
import { supabase } from '../lib/supabase';

const DEPT_COLORS = [
  '#6366f1','#ec4899','#f59e0b','#10b981',
  '#3b82f6','#8b5cf6','#ef4444','#06b6d4',
];

const TEMPLATES = {
  daycare:    { label: 'Daycare',     icon: '🏫', depts: ['Infant Room','Toddler Room','Pre-K','Kindergarten'] },
  restaurant: { label: 'Restaurant',  icon: '🍽️', depts: ['Front of House','Back of House','Bar','Counter'] },
  retail:     { label: 'Retail',      icon: '🛍️', depts: ['Sales Floor','Stockroom','Cashier'] },
  gym:        { label: 'Gym / Studio', icon: '💪', depts: ['Front Desk','Weight Room','Group Fitness','Pool'] },
  healthcare: { label: 'Healthcare',  icon: '🏥', depts: ['Emergency','General Ward','Reception','Pharmacy'] },
  custom:     { label: 'Custom',      icon: '✏️', depts: [] },
};

function makeDepts(names) {
  return names.map((name, i) => ({
    id:    Math.random().toString(36).slice(2),
    name,
    color: DEPT_COLORS[i % DEPT_COLORS.length],
  }));
}

export default function Onboarding() {
  const { org, profile, isOwner } = useAuth();
  const navigate = useNavigate();
  const [step, setStep]             = useState(1); // 1=template, 2=customize
  const [depts, setDepts]           = useState([]);
  const [saving, setSaving]         = useState(false);
  const [newName, setNewName]       = useState('');
  const [error, setError]           = useState('');
  const [checked, setChecked]       = useState(false);

  // Redirect non-owners or already-onboarded orgs
  useEffect(() => {
    if (!org || !profile) return;
    if (!isOwner) { navigate('/manager/dashboard', { replace: true }); return; }
    // If org already has departments, skip onboarding
    supabase.from('departments').select('id', { count: 'exact', head: true })
      .eq('org_id', org.id)
      .then(({ count }) => {
        if (count && count > 0) navigate('/manager/dashboard', { replace: true });
        else setChecked(true);
      });
  }, [org, profile, isOwner, navigate]);

  function pickTemplate(key) {
    setDepts(makeDepts(TEMPLATES[key].depts));
    setStep(2);
  }

  function addDept() {
    const name = newName.trim();
    if (!name) return;
    if (depts.some(d => d.name.toLowerCase() === name.toLowerCase())) {
      setError('That department already exists.');
      return;
    }
    setError('');
    setDepts(prev => [...prev, {
      id:    Math.random().toString(36).slice(2),
      name,
      color: DEPT_COLORS[prev.length % DEPT_COLORS.length],
    }]);
    setNewName('');
  }

  function removeDept(id) {
    setDepts(prev => prev.filter(d => d.id !== id));
  }

  function setColor(id, color) {
    setDepts(prev => prev.map(d => d.id === id ? { ...d, color } : d));
  }

  function setName(id, name) {
    setDepts(prev => prev.map(d => d.id === id ? { ...d, name } : d));
  }

  async function handleSave() {
    if (depts.length === 0) {
      setError('Add at least one department before continuing.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Insert named departments
      const rows = depts.map((d, i) => ({
        org_id:        org.id,
        name:          d.name.trim(),
        color:         d.color,
        display_order: i,
        is_any:        false,
      }));
      const { error: insertErr } = await supabase.from('departments').insert(rows);
      if (insertErr) throw insertErr;

      // Always insert "Any" as the last entry
      const { error: anyErr } = await supabase.from('departments').insert({
        org_id:        org.id,
        name:          'Any',
        color:         '#64748b',
        display_order: 999,
        is_any:        true,
      });
      if (anyErr) throw anyErr;

      navigate('/manager/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to save departments. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (!checked) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 20px',
    }}>
      {/* Header */}
      <div style={{ textAlign:'center', marginBottom: 32 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--accent)', marginBottom: 4 }}>
          Schedu<span style={{ color:'var(--text)' }}>Forge</span>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>
          {step === 1 ? 'What kind of business do you run?' : 'Set up your sections'}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0 }}>
          {step === 1
            ? 'Pick a template to pre-fill your departments — you can customize everything.'
            : 'These are the areas your employees will be assigned to. "Any" is always included.'}
        </p>
      </div>

      {/* Step indicator */}
      <div style={{ display:'flex', gap: 8, alignItems:'center', marginBottom: 28 }}>
        {[1, 2].map(n => (
          <div key={n} style={{
            width: n === step ? 28 : 8,
            height: 8,
            borderRadius: 4,
            background: n <= step ? 'var(--accent)' : 'var(--border)',
            transition: 'all 0.25s',
          }} />
        ))}
      </div>

      {/* Step 1: Template picker */}
      {step === 1 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 14,
          width: '100%',
          maxWidth: 600,
        }}>
          {Object.entries(TEMPLATES).map(([key, tmpl]) => (
            <button
              key={key}
              onClick={() => pickTemplate(key)}
              style={{
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius)',
                background: 'var(--surface)',
                padding: '22px 12px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = '#f5f3ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)'; }}
            >
              <span style={{ fontSize: 32 }}>{tmpl.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{tmpl.label}</span>
              {tmpl.depts.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {tmpl.depts.length} sections
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Step 2: Customize departments */}
      {step === 2 && (
        <div style={{ width: '100%', maxWidth: 520 }}>
          {/* "Any" locked row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px', borderRadius: 'var(--radius-sm)',
            background: '#f8fafc', border: '1px solid var(--border)',
            marginBottom: 8,
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: 4,
              background: '#64748b', flexShrink: 0,
            }} />
            <span style={{ flex:1, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Any</span>
            <span style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>always included</span>
          </div>

          {/* Editable departments */}
          {depts.map(dept => (
            <div key={dept.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 'var(--radius-sm)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              marginBottom: 8,
            }}>
              {/* Color picker */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 4,
                  background: dept.color, cursor: 'pointer',
                  position: 'relative',
                }} title="Click to change color">
                  <input
                    type="color"
                    value={dept.color}
                    onChange={e => setColor(dept.id, e.target.value)}
                    style={{
                      opacity: 0, position: 'absolute', inset: 0,
                      width: '100%', height: '100%', cursor: 'pointer',
                    }}
                  />
                </div>
              </div>
              <input
                value={dept.name}
                onChange={e => setName(dept.id, e.target.value)}
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  fontSize: 14, fontWeight: 500, outline: 'none',
                  color: 'var(--text)',
                }}
              />
              {/* Color swatches */}
              <div style={{ display:'flex', gap: 4, flexShrink: 0 }}>
                {DEPT_COLORS.slice(0, 5).map(c => (
                  <button
                    key={c}
                    onClick={() => setColor(dept.id, c)}
                    style={{
                      width: 14, height: 14, borderRadius: 3,
                      background: c, border: dept.color === c ? '2px solid #000' : '1px solid transparent',
                      cursor: 'pointer', padding: 0,
                    }}
                  />
                ))}
              </div>
              <button
                onClick={() => removeDept(dept.id)}
                style={{
                  flexShrink: 0, background: 'none', border: 'none',
                  cursor: 'pointer', color: 'var(--muted)', fontSize: 16,
                  padding: '0 4px', lineHeight: 1,
                }}
                title="Remove"
              >✕</button>
            </div>
          ))}

          {/* Add new department */}
          <div style={{ display:'flex', gap: 8, marginTop: 4, marginBottom: 4 }}>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDept())}
              placeholder="Add a department…"
              style={{
                flex: 1, border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', padding: '8px 12px',
                fontSize: 14, background: 'var(--surface)', color: 'var(--text)',
                outline: 'none',
              }}
            />
            <Btn onClick={addDept} size="sm">+ Add</Btn>
          </div>

          {error && (
            <div style={{
              background: '#fdecea', border: '1px solid #f5c6c6',
              borderRadius: 'var(--radius-sm)', padding: '8px 12px',
              fontSize: 13, color: '#c0392b', marginBottom: 8,
            }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 20, display:'flex', gap: 10, justifyContent:'space-between', alignItems:'center' }}>
            <button
              onClick={() => setStep(1)}
              style={{
                background: 'none', border: 'none', color: 'var(--muted)',
                cursor: 'pointer', fontSize: 13, padding: 0,
              }}
            >
              ← Back
            </button>
            <Btn variant="primary" onClick={handleSave} disabled={saving || depts.length === 0}>
              {saving ? <Spinner /> : `Save ${depts.length} section${depts.length !== 1 ? 's' : ''} & continue`}
            </Btn>
          </div>

          <p style={{ textAlign:'center', fontSize: 12, color: 'var(--muted)', marginTop: 16 }}>
            You can add, rename, or remove sections anytime from your settings.
          </p>
        </div>
      )}
    </div>
  );
}
